import React, { CSSProperties } from "react";
import { DropdownButton, Dropdown, ButtonGroup } from "react-bootstrap";
import { withTranslation, WithTranslation } from "react-i18next";
import {
  AppConfig,
  ProjectDetail,
  Files,
  ProjectStatus,
  DataGridCheckRow,
  AnyType,
  ChartDialogConfig,
} from "../../../../../common/constants";
import FileLisPanel from "./FileListPanel";
import ChartListPanel from "./ChartListPanel";
import TrimmingChartDialog from "./TrimmingChartDialog";
import UploadFileButton from "./UploadFileButton";
import { IMessageBoxParams } from "../../../../../common/MessageBox";
import { IConfirmDialogParams } from "../../../../../common/ConfirmDialog";
import { uploadFiles } from "../../../../../webapi/service/uploadFiles";
import { deleteFiles } from "../../../../../webapi/service/deleteFiles";
import {
  trimcopyFile,
  ITrimcopyFileRequestModel,
} from "../../../../../webapi/service/trimcopyFile";
import { downloadModel } from "../../../../../webapi/service/downloadModel";
import { downloadProfile } from "../../../../../webapi/service/downloadProfile";
import AuthService from "../../../../../common/AuthService";
import BackButton from "../../../common/BackButton";
import ReloadButton from "../../../../common/ReloadButton";
import DeleteButton from "../../../common/DeleteButton";
import FilesButton from "./FilesButton";
import ChartsButton from "./ChartsButton";
import { WebAPI } from "../../../../../webapi/constants";
import { IProjectRow } from "../../../../../webapi/service/getProjects";
import { IFileRow, IProtRow } from "../../../../../webapi/service/getFIles";
import { getFile, IProtData } from "../../../../../webapi/service/getFIle";
import { GridSortModel } from "@mui/x-data-grid-pro";
import "../../../../main.css";
import "../../../../../App.css";

type ProjectInfoInternal = AnyType;

export interface IProps extends WithTranslation {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * メインスペースの幅
   */
  mainSpaceWidth: number;
  /**
   * 選択されたプロジェクト情報
   */
  projectInfo: IProjectRow;
  /**
   * モデル、プロファイルメニューが選択された場合に呼び出されます
   */
  setDispMode: (mode: number) => void;
  /**
   * ファイル情報一覧
   */
  rows: IFileRow[];
  /**
   * チャート描画情報一覧
   */
  prots: IProtRow[];
  /**
   * チャート描画順
   */
  sortIds: number[];
  /**
   * DataGridソート項目
   */
  sortModel: GridSortModel;
  /**
   * DataGridのソート項目が変更された場合に呼び出されます
   * @param sortModel - DataGridソート項目
   */
  onSetSortModel: (sortModel: GridSortModel) => void;
  /**
   * ファイル並び順が変更された場合に呼び出されます
   * @param sortIds - ファイル並び順
   */
  onSetSortIds: (sortIds: number[]) => void;
  /**
   * 戻るボタンが押された場合に呼び出されます
   */
  onBack: () => void;
  /**
   * ファイル一覧をロードする場合に呼び出します
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoadFiles: (loading: boolean) => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (param: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}

interface IState {
  /**
   * ファイル一覧表示モード.
   * constants.tsxのFiles参照
   */
  fileDispMode: number;
  /**
   * トリミングダイアログを表示する場合はtrue
   */
  showTrimmingDialog: boolean;
  /**
   * チェック行ファイルID一覧
   */
  selectFileids: string[];
  /**
   * チェック行一覧
   */
  checkRows: DataGridCheckRow[];
  /**
   * 削除ボタンを表示する場合はtrue
   */
  buttonVisible: boolean;
  /**
   * ファイルがupload中の場合true
   */
  uploading: boolean;
  /**
   * 選択されたチャート描画情報
   */
  selectProt: IProtData | undefined;
  /**
   * トリミングダイアログチャート幅
   */
  width: number;
  /**
   * トリミングダイアログチャート高さ
   */
  height: number;
}

interface ICondition {
  /**
   * プロジェクト情報項目
   */
  col: string;
  /**
   * 一致条件
   */
  matchs: string[];
}

interface IMenu {
  /**
   * ラベル
   */
  label: string;
  /**
   * 表示条件一覧
   */
  conditions: ICondition[];
  /**
   * 表示画面
   */
  dispnum: number;
  /**
   * ダウンロード処理を呼び出す場合はtrue
   */
  download: boolean;
}

class FilePanel extends React.Component<IProps, IState> {
  private readonly StatusNone = ProjectStatus.NONE;
  private readonly StatusDoing = ProjectStatus.DOING;
  private readonly StatusDone = ProjectStatus.DONE;

  private readonly modelMenus: IMenu[] = [
    {
      label: "project_detail_model_create",
      conditions: [
        {
          col: "mcreatests",
          matchs: [this.StatusNone, this.StatusDoing, this.StatusDone],
        },
      ],
      dispnum: ProjectDetail.CREATE_MODEL,
      download: false,
    },
    {
      label: "project_detail_model_property",
      conditions: [{ col: "mcreatests", matchs: [this.StatusDone] }],
      dispnum: ProjectDetail.MODEL_PROPERTY,
      download: false,
    },
    {
      label: "project_detail_model_test",
      conditions: [
        { col: "mcreatests", matchs: [this.StatusDone] },
        {
          col: "mteststs",
          matchs: [this.StatusNone, this.StatusDoing, this.StatusDone],
        },
      ],
      dispnum: ProjectDetail.TEST_MODEL,
      download: false,
    },
    {
      label: "project_detail_model_results",
      conditions: [{ col: "mteststs", matchs: [this.StatusDone] }],
      dispnum: ProjectDetail.RESULT_TEST_MODEL,
      download: false,
    },
    {
      label: "project_detail_model_download",
      conditions: [{ col: "mcreatests", matchs: [this.StatusDone] }],
      dispnum: ProjectDetail.DOWNLOAD_MODEL,
      download: true,
    },
  ];
  private readonly profMenus: IMenu[] = [
    {
      label: "project_detail_profile_create",
      conditions: [
        {
          col: "pcreatests",
          matchs: [this.StatusNone, this.StatusDoing, this.StatusDone],
        },
      ],
      dispnum: ProjectDetail.CREATE_PROFILE,
      download: false,
    },
    {
      label: "project_detail_profile_results",
      conditions: [{ col: "pcreatests", matchs: [this.StatusDone] }],
      dispnum: ProjectDetail.RESULT_PROFILE,
      download: false,
    },
    {
      label: "project_detail_profile_download",
      conditions: [{ col: "pcreatests", matchs: [this.StatusDone] }],
      dispnum: ProjectDetail.DOWNLOAD_PROFILE,
      download: true,
    },
  ];

  private readonly MaxUploadFileSize = AppConfig.MAX_UPLOAD_FILE_SIZE;
  private readonly dialog_area_correction =
    ChartDialogConfig.CHAERT_DIALOG_AREA_CORRECTION;
  private readonly TrimmingChartThreshold =
    ChartDialogConfig.TRIMMING_CHART_THRESHOLD;

  constructor(props: IProps) {
    super(props);
    this.state = {
      fileDispMode: 0,
      showTrimmingDialog: false,
      selectFileids: [],
      checkRows: [],
      buttonVisible: false,
      uploading: false,
      selectProt: undefined,
      width: window.innerWidth * this.dialog_area_correction,
      height: window.innerHeight * this.dialog_area_correction,
    };

    this.handleSetFileDispMode = this.handleSetFileDispMode.bind(this);
    this.handleSelectUploadFile = this.handleSelectUploadFile.bind(this);
    this.handleDeleteFile = this.handleDeleteFile.bind(this);
    this.handleTrimming = this.handleTrimming.bind(this);
    this.handleDownloadModel = this.handleDownloadModel.bind(this);
    this.handleDownloadProfile = this.handleDownloadProfile.bind(this);
    this.handleReload = this.handleReload.bind(this);
    this.handleOpenTrimmingDialog = this.handleOpenTrimmingDialog.bind(this);
    this.handleCloseTrimmingDialog = this.handleCloseTrimmingDialog.bind(this);
    this.handleOpenDeleteDialog = this.handleOpenDeleteDialog.bind(this);
    this.handleCloseDeleteDialog = this.handleCloseDeleteDialog.bind(this);
    this.handleChangeSelectRows = this.handleChangeSelectRows.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize() {
    const { innerWidth, innerHeight } = window;
    this.setState({
      width: innerWidth * this.dialog_area_correction,
      height: innerHeight * this.dialog_area_correction,
    });
  }

  handleSetFileDispMode(mode: number) {
    if (this.state.fileDispMode !== mode) {
      this.props.resetUnoperatedTimeout();
      this.setState({ fileDispMode: mode });
    }
  }

  handleSelectUploadFile(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files) {
      return;
    }
    const {
      authService,
      projectInfo,
      rows,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    if (files.length + rows.length > AppConfig.MAX_FILES) {
      onCommunicationError(WebAPI.ID_NUM_FILE_EXCEED, undefined);
      return;
    }
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > this.MaxUploadFileSize) {
        onCommunicationError(WebAPI.ID_FILE_TOO_LARGE, undefined);
        return;
      }
    }
    onLoading(true);
    resetUnoperatedTimeout();
    const fileList: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      fileList.push(file);
    }
    const projectid = projectInfo.projectid;
    this.setState({ uploading: files.length > 0 ? true : false }, () => {
      uploadFiles({
        authService: authService,
        projectid: projectid,
        files: fileList,
        success: () => {
          this.setState({ uploading: false });
          onLoading(false);
        },
        fail: (id, status) => {
          this.setState({ uploading: false });
          onCommunicationError(id, status);
        },
      });
    });
  }

  handleDeleteFile() {
    const {
      authService,
      projectInfo,
      onLoadFiles,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    const { selectFileids } = this.state;
    this.handleCloseDeleteDialog();
    onLoading(true);
    resetUnoperatedTimeout();
    deleteFiles({
      authService: authService,
      projectid: projectInfo.projectid,
      fileids: selectFileids,
      success: () => {
        this.handleChangeSelectRows([]);
        onLoadFiles(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleTrimming(trimcopy: ITrimcopyFileRequestModel) {
    const {
      authService,
      projectInfo,
      onLoadFiles,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    const { selectProt } = this.state;
    if (selectProt) {
      const { name, trimstart, trimend } = trimcopy;
      onLoading(true);
      resetUnoperatedTimeout();
      trimcopyFile({
        authService: authService,
        projectid: projectInfo.projectid,
        fileid: selectProt.fileid,
        name: name,
        trimstart: trimstart,
        trimend: trimend,
        success: () => {
          this.handleCloseTrimmingDialog();
          onLoadFiles(false);
        },
        fail: (id, status) => {
          onCommunicationError(id, status);
        },
      });
    }
  }

  handleDownloadModel() {
    const {
      authService,
      projectInfo,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    const projectid = projectInfo.projectid;
    resetUnoperatedTimeout();
    downloadModel({
      authService: authService,
      projectid: projectid,
      filename: "model_" + projectid,
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleDownloadProfile() {
    const {
      authService,
      projectInfo,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    const projectid = projectInfo.projectid;
    resetUnoperatedTimeout();
    downloadProfile({
      authService: authService,
      projectid: projectid,
      filename: "profile_" + projectid,
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleReload() {
    this.props.resetUnoperatedTimeout();
    this.props.onLoadFiles(true);
  }

  handleOpenTrimmingDialog(rowid: number) {
    const {
      authService,
      projectInfo,
      rows,
      onCommunicationError,
      resetUnoperatedTimeout,
      onLoading,
    } = this.props;
    const projectid = projectInfo.projectid;
    const row = rows.find((row) => {
      return row.id === rowid;
    });
    if (row) {
      onLoading(true);
      resetUnoperatedTimeout();
      getFile({
        authService: authService,
        projectid: projectid,
        fileid: row.fileid,
        name: row.name,
        datanum: row.datanum,
        threshold: this.TrimmingChartThreshold,
        includeEnd: true,
        isRetry401: true,
        success: (prot) => {
          this.setState({
            showTrimmingDialog: true,
            selectProt: prot,
          });
          onLoading(false);
        },
        fail: (id, status) => {
          onCommunicationError(id, status);
        },
      });
    }
  }

  handleCloseTrimmingDialog() {
    this.setState({
      showTrimmingDialog: false,
      selectProt: undefined,
    });
  }

  getRow(id: number) {
    return this.props.rows.find((row) => {
      return row.id === id;
    });
  }

  handleOpenDeleteDialog() {
    const { showConfirm, t } = this.props;
    const fileids: string[] = [];
    this.state.checkRows.forEach((row) => {
      const rowData = this.getRow(row);
      if (rowData) {
        fileids.push(rowData.fileid);
      }
    });
    this.setState({ selectFileids: fileids });
    showConfirm({
      title: t("project_detail_delete_confirm"),
      message: t("project_detail_delete_confirm_message"),
      onOk: this.handleDeleteFile,
      onCancel: this.handleCloseDeleteDialog,
    });
  }

  handleCloseDeleteDialog() {
    this.setState({ selectFileids: [] });
  }

  handleChangeSelectRows(selects: DataGridCheckRow[]) {
    this.setState({
      checkRows: selects,
      buttonVisible: selects.length > 0 ? true : false,
    });
  }

  isMatch(conditions: ICondition[], projectInfo: ProjectInfoInternal) {
    const matchAry: boolean[] = [];
    conditions.forEach((condition) => {
      const { col, matchs } = condition;
      const match = matchs.find((match) => {
        return match === projectInfo[col];
      });
      matchAry.push(col === "" || match ? true : false);
    });
    const matchs = matchAry.filter((match) => {
      return match === true;
    });
    return matchAry.length === matchs.length;
  }

  getButtonMenu(menuItems: IMenu[], onDownLoad: () => void) {
    const { projectInfo, setDispMode, t } = this.props;
    const menus: JSX.Element[] = [];
    menuItems.forEach((menu, i) => {
      if (this.isMatch(menu.conditions, projectInfo)) {
        if (menu.download) {
          menus.push(
            <Dropdown.Item as="button" onClick={onDownLoad} key={i}>
              {t(menu.label)}
            </Dropdown.Item>
          );
        } else {
          menus.push(
            <Dropdown.Item
              as="button"
              onClick={() => setDispMode(menu.dispnum)}
              key={i}
            >
              {t(menu.label)}
            </Dropdown.Item>
          );
        }
      }
    });
    return menus;
  }

  getMenuButton(title: string, menuItems: IMenu[], onDownLoad: () => void) {
    const menus = this.getButtonMenu(menuItems, onDownLoad);
    const menuButton =
      menus.length > 0 ? (
        <DropdownButton className="btn-mainspace" title={title}>
          {menus}
        </DropdownButton>
      ) : (
        ""
      );
    return menuButton;
  }

  getContents() {
    let contents = <div></div>;
    switch (this.state.fileDispMode) {
      case Files.FILE_LIST:
        contents = (
          <FileLisPanel
            rows={this.props.rows}
            checkRows={this.state.checkRows}
            onChangeSelectRows={this.handleChangeSelectRows}
            onSelecRow={this.handleOpenTrimmingDialog}
            sortModel={this.props.sortModel}
            onSetSortModel={this.props.onSetSortModel}
            onSetSortIds={this.props.onSetSortIds}
          />
        );
        break;
      case Files.CHART_LIST:
        contents = (
          <ChartListPanel
            mainSpaceWidth={this.props.mainSpaceWidth}
            rows={this.props.rows}
            prots={this.props.prots}
            checkRows={this.state.checkRows}
            sortIds={this.props.sortIds}
            onChangeSelectRows={this.handleChangeSelectRows}
            onSelecRow={this.handleOpenTrimmingDialog}
            onLoading={this.props.onLoading}
          />
        );
        break;
    }
    return contents;
  }

  render() {
    const { projectInfo, rows, onBack, t } = this.props;
    const {
      uploading,
      buttonVisible,
      showTrimmingDialog,
      selectProt,
      width,
      height,
    } = this.state;
    const contents = this.getContents();
    const idAddFileEnabled = rows.length < AppConfig.MAX_FILES && !uploading;
    const uploadBtn = idAddFileEnabled ? (
      <UploadFileButton onChange={this.handleSelectUploadFile} />
    ) : (
      ""
    );
    const deleteBtn = buttonVisible ? (
      <DeleteButton onClick={this.handleOpenDeleteDialog} />
    ) : (
      ""
    );
    const modelBtn = this.getMenuButton(
      t("project_detail_model"),
      this.modelMenus,
      this.handleDownloadModel
    );
    const profileBtn = this.getMenuButton(
      t("project_detail_profile"),
      this.profMenus,
      this.handleDownloadProfile
    );
    const dialog: CSSProperties | undefined = showTrimmingDialog
      ? undefined
      : { display: "none" };
    return (
      <div className="mainspace">
        <div className="mainspace-title">
          <h3>{t("project_list_name")}</h3>
          <h3 style={{ marginLeft: "10px" }}>{projectInfo.name}</h3>
        </div>
        <div className="mainspace-toolbar">
          <BackButton onClick={onBack} />
          <ReloadButton onClick={this.handleReload} />
          {uploadBtn}
          {deleteBtn}
          {modelBtn}
          {profileBtn}
        </div>
        <div className="mainspace-toolbar">
          <h3>{t("project_detail_files")}</h3>
          <ButtonGroup>
            <FilesButton
              onClick={() => this.handleSetFileDispMode(Files.FILE_LIST)}
            />
            <ChartsButton
              onClick={() => this.handleSetFileDispMode(Files.CHART_LIST)}
            />
          </ButtonGroup>
        </div>
        <div>{contents}</div>
        <div className="overlay" style={dialog}>
          <TrimmingChartDialog
            show={showTrimmingDialog}
            width={width}
            height={height}
            edit={idAddFileEnabled}
            prot={selectProt}
            onOk={this.handleTrimming}
            onCancel={this.handleCloseTrimmingDialog}
          />
        </div>
      </div>
    );
  }
}

/**
 * ファイルメイン画面
 */
export default withTranslation()(FilePanel);
